// src/pages/TradingData/SignalPickerModal.jsx
// Modal: strategy list → beautiful indicator card → select signal
// Supports filter mode: card generates both preceding + exclude signals

import React, { useState } from 'react';
import {
  Box, Typography, Stack, IconButton, Dialog, DialogContent, alpha,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { MONO } from './IndicatorConfigs';
import INDICATOR_CONFIGS from './IndicatorConfigs';
import IndicatorCard from './IndicatorCard';

export default function SignalPickerModal({ open, onClose, onSelect, roleLabel, roleColor, filterMode = false, companionMode = false, defaults = {} }) {
  const [selectedKey, setSelectedKey] = useState(null);

  const handleClose = () => { setSelectedKey(null); onClose(); };
  const handleBack = () => setSelectedKey(null);
  const handleSelect = (result) => {
    onSelect(result); // string for normal mode, { preceding, exclude } for filter mode
    setSelectedKey(null);
    onClose();
  };

  const activeAccent = selectedKey ? INDICATOR_CONFIGS[selectedKey].accent : roleColor;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: {
        bgcolor: '#080c14', border: `1px solid ${alpha(activeAccent, 0.2)}`,
        borderRadius: 3, maxHeight: '90vh', overflow: 'hidden', transition: 'border-color 0.3s',
      } }}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${alpha('#fff', 0.06)}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {selectedKey && (
            <Box onClick={handleBack} sx={{
              px: 1, py: 0.3, fontSize: '0.65rem', fontFamily: MONO,
              color: alpha('#fff', 0.4), border: `1px solid ${alpha('#fff', 0.1)}`,
              borderRadius: 1, cursor: 'pointer', userSelect: 'none',
              '&:hover': { bgcolor: alpha('#fff', 0.05) },
            }}>
              ← Back
            </Box>
          )}
          <Box sx={{
            width: 10, height: 10, borderRadius: '50%', bgcolor: activeAccent,
            boxShadow: `0 0 10px ${alpha(activeAccent, 0.6)}`, transition: 'all 0.3s',
          }} />
          <Typography sx={{
            color: activeAccent, fontSize: '0.78rem', fontWeight: 800,
            fontFamily: MONO, letterSpacing: '0.1em', transition: 'color 0.3s',
          }}>
            {selectedKey
              ? INDICATOR_CONFIGS[selectedKey].label.toUpperCase()
              : filterMode
                ? 'PICK FILTER SIGNAL'
                : `PICK ${roleLabel} SIGNAL`}
          </Typography>
        </Stack>
        <IconButton onClick={handleClose} sx={{ color: alpha('#fff', 0.3) }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Strategy List */}
        {!selectedKey && (
          <Box sx={{ py: 0.5 }}>
            {Object.entries(INDICATOR_CONFIGS).map(([key, config]) => (
              <Box key={key} onClick={() => setSelectedKey(key)}
                sx={{
                  px: 2.5, py: 1.6,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', transition: 'all 0.15s',
                  borderBottom: `1px solid ${alpha('#fff', 0.03)}`,
                  '&:hover': { bgcolor: alpha(config.accent, 0.06) },
                }}>
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: config.accent, boxShadow: `0 0 8px ${alpha(config.accent, 0.5)}` }} />
                  <Typography sx={{ color: '#e2e8f0', fontSize: '0.88rem', fontWeight: 600, fontFamily: MONO }}>
                    {config.label}
                  </Typography>
                </Stack>
                <Typography sx={{ color: alpha(config.accent, 0.4), fontSize: '0.75rem', fontFamily: MONO }}>→</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Card Detail */}
        {selectedKey && (
          <Box sx={{ p: 2 }}>
            <IndicatorCard
              indicatorKey={selectedKey}
              config={INDICATOR_CONFIGS[selectedKey]}
              onSelect={handleSelect}
              roleColor={roleColor}
              filterMode={filterMode}
              companionMode={companionMode}
              defaults={defaults}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
